import { Subscription, combineLatest } from 'rxjs';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { EstateData } from 'src/lib/fliko/models/estate/estate.data';
import { ResidentData } from 'src/lib/fliko/models/person/resident.data';
import { RentalUnitType } from 'src/lib/fliko/models/units/unit.data';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { NoteVM } from 'src/app/shared/models/notes.view-model';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { NotesDataService } from 'src/app/shared/services/notes-data.service';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { CommApartmentExportReport } from '../../../reports/comm-apartment.export-report.class';
import { SuperAdminResidentsExportReport } from '../../../reports/superadmin-residents.export-report';
import { SuperAdminUnitsExportReport } from '../../../reports/superadmin-units.export-report';
import { UserCommList } from '../../../services/models/users-table.data';
import { TenancyViewModel, UnitCommResidencyViewModel } from '../../../services/models/users-view-models';
import { UsersCommDataService } from '../../../services/users-comm-data.service';

@Component({
  selector: 'app-users-comm-apartment-report',
  templateUrl: './users-comm-apartment-report.component.html',
  styleUrls: ['./users-comm-apartment-report.component.scss']
})
export class UsersCommApartmentReportComponent implements OnInit, OnDestroy {
  public dataLoaded = false;
  public isSuperAdmin = false;

  private estate!: EstateData;
  private apartments: UnitCommResidencyViewModel[] = [];
  private parkings: UnitCommResidencyViewModel[] = [];
  private storages: UnitCommResidencyViewModel[] = [];
  private landlords: UserCommList.ResidentElement[] = [];
  private tenants: UserCommList.ResidentElement[] = [];
  private tenancies: TenancyViewModel[] = [];
  private notes = new Map<string, NoteVM<ResidentData>[]>();
  private tenantsEnabled = false;

  private subscription$ = new Subscription();

  constructor(
    private dialogRef: MatDialogRef<UsersCommApartmentReportComponent>,
    private ctxDS: ContextDataService,
    private userCommDS: UsersCommDataService,
    private errorService: ErrorHandlingService,
    private notesDS: NotesDataService,
    private ls: LocalizationService
  ) {}

  ngOnInit() {
    this.subscription$.add(
      combineLatest([
        combineLatest([
          this.userCommDS.getUnitsResidencyData$(RentalUnitType.APARTMENT),
          this.userCommDS.getUnitsResidencyData$(RentalUnitType.PARKING),
          this.userCommDS.getUnitsResidencyData$(RentalUnitType.STORAGE)
        ]),
        this.userCommDS.getLandlordsTabData$(),
        this.userCommDS.getTenantsTabData$(),
        this.userCommDS.getTenanciesData$(),
        this.notesDS.getResidentNotes$()
      ]).subscribe(([[apartments, parkings, storages], landlords, tenants, tenancies, notes]) => {
        this.apartments = apartments;
        this.parkings = parkings;
        this.storages = storages;
        this.landlords = landlords ?? [];
        this.tenants = tenants ?? [];
        this.tenancies = tenancies ?? [];
        this.notes = MapHlp.idMapArray(notes, ['context.id'] as any);
        this.dataLoaded = true;
      })
    );

    this.estate = this.ctxDS.peekCurrentEstate()!;
    const perms = this.ctxDS.peekPerms();
    this.isSuperAdmin = perms?.superAdmin ?? false;
    this.tenantsEnabled = !!perms?.feat?.mobile?.communityTenants;
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public generateReport(): void {
    if (this.dataLoaded) {
      const reportGen = new CommApartmentExportReport(
        this.ls,
        this.estate,
        this.apartments,
        this.parkings,
        this.storages,
        this.landlords,
        this.tenants,
        this.tenancies,
        this.notes
      );
      reportGen.generateRaport(this.tenantsEnabled);

      this.dialogRef.close();
    } else {
      this.errorService.showGlobalError({ key: 'error.noData' });
    }
  }

  public generateLandlords(): void {
    if (this.dataLoaded) {
      const gen = new SuperAdminResidentsExportReport(this.ls, this.estate, this.landlords, this.tenants);
      gen.generateReport();
    } else {
      this.errorService.showGlobalError({ key: 'error.noData' });
    }
  }

  public generateUnits(): void {
    if (this.dataLoaded) {
      const gen = new SuperAdminUnitsExportReport(this.ls, this.estate, this.apartments, this.parkings, this.storages);
      gen.generateReport();
    } else {
      this.errorService.showGlobalError({ key: 'error.noData' });
    }
  }
}
